import { ref } from "vue";
import { defineStore } from 'pinia';
import ReferenceService from "@/services/ReferenceService";
import { normalizeResourceAllocationGroups } from "@/utils/resourceAllocations";
import { reconcileSavedResourceSchedule } from "@/utils/savedResourceSchedule";

const clonePayload = (value) => {
  if (value === undefined) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(value));
};

const getAddressText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return `${value}`;
  }

  if (typeof value !== "object") {
    return "";
  }

  return getAddressText(
    value.data
      ?? value.location_address
      ?? value.address?.data
      ?? value.label
      ?? "",
  );
};

const normalizeAddressText = (value) => getAddressText(value)
  .trim()
  .replace(/\s+/g, " ");

const normalizePersonnelAssignmentForManage = (assignment = {}) => ({
  id: assignment?.id ?? null,
  product_personnel_requirement_id:
    assignment?.product_personnel_requirement_id ?? null,
  staff_id: assignment?.staff_id ?? assignment?.staff?.id ?? null,
});

const normalizeResourceEntryForManage = (
  resource = {},
  { includePersonnelAssignments = true } = {},
) => {
  const payload = {
    id: resource?.id ?? null,
    product_id: resource?.product_id ?? resource?.product?.id ?? null,
    quantity: resource?.quantity ?? 1,
  };

  if (includePersonnelAssignments) {
    payload.personnel_assignments = Array.isArray(resource?.personnel_assignments)
      ? resource.personnel_assignments.map(normalizePersonnelAssignmentForManage)
      : [];
  }

  return payload;
};

const normalizeDateEntryForManage = (
  entry = {},
  {
    includeResources = true,
    includeTimesheets = true,
    includePersonnelAssignments = true,
  } = {},
) => {
  const payload = {
    id: entry?.id ?? entry?.date_id ?? null,
    date_id: entry?.date_id ?? entry?.id ?? null,
    date: entry?.date ?? null,
    startTime: entry?.startTime ?? null,
    finishTime: entry?.finishTime ?? null,
    location_name: entry?.location_name ?? null,
    location_address: normalizeAddressText(entry?.location_address ?? entry?.address?.data) || null,
    address_id: entry?.address_id ?? entry?.address?.id ?? null,
  };

  if (includeResources) {
    payload.resources = Array.isArray(entry?.resources)
      ? entry.resources.map((resource) =>
        normalizeResourceEntryForManage(resource, { includePersonnelAssignments }))
      : [];
  }

  if (includeTimesheets && Array.isArray(entry?.timesheets)) {
    payload.timesheets = clonePayload(entry.timesheets);
  }

  return payload;
};

const normalizeLocationEntryForManage = (entry = {}) => ({
  id: entry?.id ?? null,
  address_id: entry?.address_id ?? entry?.address?.id ?? null,
  location_address: normalizeAddressText(entry?.location_address ?? entry?.address?.data ?? entry?.data) || null,
  latitude: entry?.address?.latitude ?? entry?.latitude ?? null,
  longitude: entry?.address?.longitude ?? entry?.longitude ?? null,
});

const normalizeVersionEntryForManage = (version = {}, options = {}) => ({
  id: version?.id ?? null,
  resource_revision: Number(version?.resource_revision ?? 0),
  locations: Array.isArray(version?.locations)
    ? version.locations.map(normalizeLocationEntryForManage)
    : [],
  dates: Array.isArray(version?.dates)
    ? version.dates.map((entry) => normalizeDateEntryForManage(entry, options))
    : [],
});

const buildManagePayload = (referenceData = {}, { versions, includeResources = true, includeTimesheets = true, includePersonnelAssignments = true } = {}) => {
  const selectedVersions = Array.isArray(versions) ? versions : referenceData?.versions;

  return {
    id: referenceData?.id ?? null,
    name: referenceData?.name ?? null,
    customer_id: referenceData?.customer_id ?? referenceData?.customer?.id ?? null,
    notes: referenceData?.notes ?? null,
    description: referenceData?.description ?? null,
    versions: Array.isArray(selectedVersions)
      ? selectedVersions.map((version) => normalizeVersionEntryForManage(version, {
        includeResources,
        includeTimesheets,
        includePersonnelAssignments,
      }))
      : [],
  };
};

const normalizeDateEntry = (entry = {}) => ({
  ...entry,
  id: entry?.id ?? entry?.date_id ?? null,
  date_id: entry?.date_id ?? entry?.id ?? null,
  resources: normalizeResourceAllocationGroups(
    Array.isArray(entry?.resources) ? entry.resources : [],
  ),
  timesheets: Array.isArray(entry?.timesheets) ? entry.timesheets : [],
});

const normalizeVersionEntry = (version = {}) => ({
  ...version,
  locations: Array.isArray(version?.locations) ? version.locations : [],
  dates: Array.isArray(version?.dates) ? version.dates.map(normalizeDateEntry) : [],
});

const normalizeReferenceResponse = (payload = {}) => {
  const versions = payload?.data?.versions;
  if (!Array.isArray(versions)) {
    return payload;
  }

  return {
    ...payload,
    data: {
      ...payload.data,
      versions: versions.map(normalizeVersionEntry),
    },
  };
};

export let useReferenceStore = defineStore('reference', {
  state() {
    return {
      referenceType: null,
      customerId: null,
      referenceId: null,
      referenceService: null,
      referenceData: ref({}),
      activeVersion: 0,
      loading: true,
      entries: [],
    };
  },

  actions: {
    configure(referenceId) {
      this.referenceId = referenceId
      this.referenceService = new ReferenceService(this.referenceId);
      this.fetch();
    },

    setType(type) {
      this.referenceType = type;
    },

    async acceptVersion() {
      const response = await this.referenceService.acceptVersion(this.getActiveVersionId)
      this.referenceData.value = normalizeReferenceResponse(response.data)
    },

    async cancelVersion() {
      const response = await this.referenceService.cancelVersion(this.getActiveVersionId)
      this.referenceData.value = normalizeReferenceResponse(response.data)
    },

    async fetch() {
      try {
        this.loading = true
        const data = await this.referenceService.get();
        this.referenceData.value = normalizeReferenceResponse(data.data);
        this.entries = this.resources;
        this.activeVersion = 0;
        this.loading = false
      } catch (error) {
        console.error(error);
      }
    },

    async update(payload = undefined) {
      try {
        const response = await this.referenceService.update(payload ?? this.buildManagePayload())
        const revisions = response?.data?.resource_revisions ?? {}
        ;(this.referenceData.value?.data?.versions ?? []).forEach((version) => {
          if (Object.prototype.hasOwnProperty.call(revisions, String(version.id))) {
            version.resource_revision = Number(revisions[String(version.id)])
          }
        })
        return response
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    async updateLead(leadStaffId) {
      const response = await this.referenceService.updateLead(leadStaffId)
      this.referenceData.value.data.lead_staff_id = response.data?.lead_staff_id ?? null
      this.referenceData.value.data.lead_staff = response.data?.lead_staff ?? null
      return response
    },

    async refreshResourceSchedule(versionId, submittedVersion, submittedRows) {
      const response = await this.referenceService.get();
      const savedVersion = normalizeReferenceResponse(response.data)?.data?.versions
        ?.find(version => Number(version.id) === Number(versionId));
      const currentVersion = this.data.versions?.find(version => Number(version.id) === Number(versionId));
      if (!savedVersion || !currentVersion) {
        throw new Error('Unable to reload the saved schedule.');
      }
      if (Number(savedVersion.resource_revision ?? 0) !== Number(currentVersion.resource_revision ?? 0)) {
        throw new Error('Another user changed this schedule after it was saved. Reload the latest schedule before saving again.');
      }
      reconcileSavedResourceSchedule(currentVersion, submittedVersion, submittedRows, savedVersion);
      return savedVersion;
    },

    async destroy(payload = {}) {
      try {
        return await this.referenceService.destroy(payload)
      } catch (error) {
        console.error(error)
        throw error
      }
    },

    buildManagePayload(options = {}) {
      return buildManagePayload(this.data, options);
    },

    addDateProduct(date, startTime, finishTime, product, qty = 0) {
      const resources = Array.from({ length: qty }, () => ({ product, product_id: product?.id ?? null }));

      // Find the existing date entry
      const dates = this.referenceData.value.data.versions[this.activeVersion].dates;
      const existingDate = dates.find(d =>
        new Date(d.date).toISOString().split('T')[0] === date &&
        d.start.slice(0, 5) === startTime &&
        d.finish.slice(0, 5) === finishTime
      );

      if (existingDate) {
        // If the entry exists, merge resources
        existingDate.resources.push(...resources);
      } else {
        // If the entry does not exist, create a new one
        dates.push({
          date: date,
          start: startTime,
          finish: finishTime,
          resources: resources,
        });
      }
    },

    getDuration(start, end) {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      return ((endMinutes - startMinutes) / 60).toFixed(1);
    },

    deleteDateIndex(index) {
      this.referenceData.value.data.versions[this.activeVersion].dates.splice(index, 1);
    },

    setCustomer(customer) {
      this.referenceData.value.data.customer_id = customer.id
      this.referenceData.value.data.customer = customer
    },

    setActiveVersionEntries(newEntries) {
      const normalizedEntries = normalizeVersionEntry(newEntries)
      this.referenceData.value.data.versions[this.activeVersion] = normalizedEntries;
      this.entries = normalizedEntries;
    },
  },

  getters: {
    isLoading() {
      return this.loading
    },

    id() {
      return this.referenceId;
    },

    name() {
      return this.referenceData.value?.data.name;
    },

    data() {
      return this.referenceData.value?.data ?? {};
    },

    getActiveVersionId() {
      return this.referenceData.value?.data?.versions?.[this.activeVersion]?.id ?? null;
    },

    resources() {
      return this.referenceData.value?.data?.versions?.[this.activeVersion] ?? { dates: [] };
    }
  }
});
